import { useState, useEffect, useRef } from 'react';

const HERO_VIDEO_URL = '/videos/hero.mp4';

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); io.unobserve(el); } },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const delayClass =
    delay === 100 ? 'delay-[100ms]' :
    delay === 200 ? 'delay-[200ms]' :
    delay === 300 ? 'delay-[300ms]' :
    delay === 400 ? 'delay-[400ms]' : '';

  return (
    <div
      ref={ref}
      className={`transition-[opacity,transform] duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${delayClass} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-7'
      } ${className}`}
    >
      {children}
    </div>
  );
}

// ── Reused icon container ───────────────────────────────────────────────────
function IconBox({ children, color = 'primary' }) {
  const cls = color === 'error'
    ? 'bg-[rgba(196,82,74,0.15)] border-[rgba(196,82,74,0.3)]'
    : 'bg-primary-400/15 border-primary-400/30';
  return (
    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center mb-6 flex-shrink-0 ${cls}`}>
      {children}
    </div>
  );
}

function FeatureTag({ label }) {
  return (
    <span className="text-[0.75rem] font-semibold tracking-[0.04em] px-[10px] py-1 rounded-sm bg-primary-400/15 text-primary-300 border border-primary-400/25">
      {label}
    </span>
  );
}

export default function LandingPage({ onNavigate }) {
  const [navScrolled, setNavScrolled] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const io = new IntersectionObserver(
      ([entry]) => setNavScrolled(!entry.isIntersecting),
      { threshold: 0 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, []);


  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">

      {/* ── Fixed Nav ──────────────────────────────────────────────────── */}
      <nav className={`fixed top-0 left-0 right-0 z-[100] grid grid-cols-3 items-center px-5 md:px-12 py-[14px] bg-sand-50 transition-[box-shadow] duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
        navScrolled ? 'shadow-md' : 'shadow-none'
      }`}>

        <div className="flex items-center gap-6">
          <button type="button" onClick={() => onNavigate('landing')} className="flex items-center gap-[10px] focus:outline-none">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" className="flex-shrink-0">
              <rect width="32" height="32" rx="8" fill="#36A85B"/>
              <g transform="translate(6 6) scale(0.4167)" stroke="#FFFFFF" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M24 5 C 13.5 13, 13.5 31, 24 43 C 34.5 31, 34.5 13, 24 5 Z"/>
                <path d="M17.5 25 L22 30 L31 16.5"/>
              </g>
            </svg>
            <span className="text-[1.0625rem] font-bold text-neutral-900 tracking-[-0.01em]">Clean Shopper</span>
          </button>
          <button type="button" onClick={() => onNavigate('landing')}
            className="hidden md:block text-small text-neutral-500 hover:text-neutral-900 transition-colors duration-fast focus:outline-none">
            Home
          </button>
          <button type="button" onClick={() => onNavigate('home')}
            className="hidden md:block text-small text-neutral-500 hover:text-neutral-900 transition-colors duration-fast focus:outline-none">
            Browse
          </button>
        </div>

        <div className="hidden md:flex justify-center items-center gap-3">
          <form onSubmit={(e) => { e.preventDefault(); onNavigate('signin'); }} className="relative w-full max-w-[320px]">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              placeholder="Search products..."
              className="w-full pl-9 pr-4 py-[5px] text-small text-neutral-800 placeholder:text-neutral-400 bg-white border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-fast"
            />
          </form>
          <button
            type="button"
            onClick={() => onNavigate('signin')}
            className="flex items-center gap-[6px] px-4 py-[5px] text-small font-semibold text-primary-600 bg-primary-100 hover:bg-primary-200 border border-primary-200 rounded-md transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-500 whitespace-nowrap"
          >
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2Z"/><path d="M12 8v4l3 3"/>
            </svg>
            Ask AI
          </button>
        </div>

        <div className="flex justify-end items-center gap-1">
          <button type="button" onClick={() => onNavigate('signin')} aria-label="Saved products"
            className="p-2 text-neutral-500 hover:text-neutral-900 transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
          <button type="button" onClick={() => onNavigate('signin')} aria-label="Profile"
            className="p-2 text-neutral-500 hover:text-neutral-900 transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
            </svg>
          </button>
          <button type="button" onClick={() => onNavigate('signin')}
            className="inline-flex items-center bg-primary-400 hover:bg-primary-500 text-white text-small font-bold tracking-[0.01em] px-[22px] py-[6px] rounded-md transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-sand-50">
            Log In
          </button>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section id="hero" className="relative w-full h-screen min-h-[640px] overflow-hidden flex items-end">
        <div ref={sentinelRef} className="absolute top-0 left-0 h-px w-full" aria-hidden="true" />

        {HERO_VIDEO_URL ? (
          <video className="absolute inset-0 w-full h-full object-cover object-center" src={HERO_VIDEO_URL}
            autoPlay muted loop playsInline />
        ) : (
          <div className="absolute inset-0 bg-primary-900" />
        )}

        <div className="absolute inset-0 [background:linear-gradient(to_bottom,rgba(10,18,14,0.28)_0%,rgba(10,18,14,0.12)_35%,rgba(10,18,14,0.55)_70%,rgba(10,18,14,0.88)_100%)]" />

        <div className="relative z-10 w-full px-5 md:px-12 pb-16 md:pb-20 max-w-[780px]">
          <div className="inline-flex items-center gap-2 bg-primary-400/[0.22] border border-primary-400/45 text-primary-300 text-[0.75rem] font-bold tracking-[0.1em] uppercase px-[14px] py-[6px] rounded-full mb-6">
            <span className="w-[6px] h-[6px] bg-primary-400 rounded-full" aria-hidden="true" />
            AI-powered ingredient research
          </div>

          <h1 className="text-[clamp(2.4rem,6vw,5.2rem)] font-extrabold leading-[1.0] tracking-[-0.03em] text-white mb-6">
            Know exactly<br />what's <em className="not-italic text-primary-300">in it.</em>
          </h1>

          <p className="text-[1.125rem] font-normal leading-[1.6] text-white/70 max-w-[520px] mb-9">
            Clean Shopper decodes ingredient lists, flags what's harmful, and finds safer alternatives — instantly. No more guesswork at the label.
          </p>

          <button type="button" onClick={() => onNavigate('signin')}
            className="inline-flex items-center gap-2 bg-primary-400 hover:bg-primary-500 text-white text-body font-bold tracking-[0.01em] px-8 py-[14px] rounded-md transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-primary-900">
            Explore Products
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <p className="text-[0.75rem] text-white/40 mt-4 tracking-[0.02em]">
            No account required to try &nbsp;·&nbsp; Free forever for personal use
          </p>
        </div>
      </section>

      {/* ── Trust Bar ──────────────────────────────────────────────────── */}
      <div className="bg-primary-900 border-b border-white/[0.06] px-5 md:px-12 py-5 flex items-center gap-10 overflow-x-auto">
        <span className="text-[0.6875rem] font-bold tracking-[0.1em] uppercase text-white/30 whitespace-nowrap flex-shrink-0">
          Powered by
        </span>
        <div className="w-px h-6 bg-white/[0.12] flex-shrink-0" aria-hidden="true" />
        <div className="flex items-center gap-9 flex-wrap">
          {[
            { label: 'Claude AI', d: null, shape: 'circle' },
            { label: 'Real-time web research', d: null, shape: 'check' },
            { label: 'Curated product catalog', d: null, shape: 'rect' },
            { label: 'Ingredient safety analysis', d: null, shape: 'star' },
          ].map(({ label, shape }) => (
            <div key={label} className="flex items-center gap-2 text-white/50 text-[0.8125rem] font-semibold tracking-[0.02em] whitespace-nowrap">
              <div className="w-[22px] h-[22px] rounded-full bg-primary-400/20 border border-primary-400/35 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  {shape === 'circle'  && <circle cx="6" cy="6" r="4" stroke="#28A368" strokeWidth="1.5"/>}
                  {shape === 'star'    && <path d="M6 1l1.12 2.27 2.5.36-1.81 1.76.43 2.49L6 6.77 3.76 7.88l.43-2.49L2.38 3.63l2.5-.36L6 1z" fill="#28A368"/>}
                  {shape === 'rect'    && <rect x="2" y="2" width="8" height="8" rx="2" stroke="#28A368" strokeWidth="1.5"/>}
                  {shape === 'check'   && <path d="M2 6l3 3 5-5" stroke="#28A368" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>}
                </svg>
              </div>
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* ── Problem ────────────────────────────────────────────────────── */}
      <section id="problem" className="bg-primary-900 px-5 md:px-12 py-20 md:py-[120px]">
        <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">

          <div>
            <Reveal>
              <p className="text-[0.6875rem] font-bold tracking-[0.12em] uppercase text-primary-400 mb-5">The problem</p>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="text-[clamp(2rem,4vw,3.4rem)] font-extrabold leading-[1.05] tracking-[-0.03em] text-white mb-6">
                The label<br />is not on<br />your side.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="text-[1.0625rem] leading-[1.7] text-white/60 max-w-[520px]">
                "Clean," "natural," "non-toxic" — these claims are unregulated. Companies can print them on anything. Meanwhile, ingredient lists are designed to be unreadable: chemical names no one can interpret, buried in fine print below a reassuring stock photo.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <p className="text-[0.9375rem] leading-[1.7] text-white/30 mt-4 max-w-[520px]">
                You've done the research. You've read the blogs. You still can't be sure. That's not a skill gap — it's a design problem.
              </p>
            </Reveal>
          </div>

          <div className="flex flex-col gap-6">
            {[
              {
                title: 'Unreadable ingredient lists',
                body: 'Chemical names require a PhD to interpret. Most labels are written to obscure, not inform.',
                icon: <><path d="M9 3v6M9 13v.5" stroke="#C4524A" strokeWidth="2" strokeLinecap="round"/><circle cx="9" cy="9" r="7" stroke="#C4524A" strokeWidth="1.5"/></>,
              },
              {
                title: 'Misleading marketing claims',
                body: '"Green," "natural," and "non-toxic" are legally meaningless. Anyone can print them on anything.',
                icon: <><path d="M3 9h12M9 3l6 6-6 6" stroke="#C4524A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></>,
              },
              {
                title: 'Fragmented, unreliable research',
                body: 'EWG, Reddit, brand sites, wellness blogs — the truth is scattered and contradictory.',
                icon: <><circle cx="9" cy="9" r="6" stroke="#C4524A" strokeWidth="1.5"/><path d="M6 6c0-1.66 1.34-3 3-3s3 1.34 3 3c0 1.5-1.5 2.5-3 3" stroke="#C4524A" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="14" r=".5" fill="#C4524A"/></>,
              },
              {
                title: 'Preferences with nowhere to live',
                body: "You know which ingredients to avoid. You've built a mental list. It lives only in your head.",
                icon: <path d="M4 9h2l2-4 2 8 2-4h2" stroke="#C4524A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>,
              },
            ].map(({ title, body, icon }, i) => (
              <Reveal key={title} delay={i * 100}>
                <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-6 flex gap-4 items-start hover:bg-white/[0.07] hover:border-primary-400/30 transition-[background,border-color] duration-normal">
                  <div className="w-10 h-10 rounded-[10px] bg-[rgba(196,82,74,0.15)] border border-[rgba(196,82,74,0.3)] flex items-center justify-center flex-shrink-0">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">{icon}</svg>
                  </div>
                  <div>
                    <h4 className="text-[0.9375rem] font-bold text-white tracking-[-0.01em] mb-1">{title}</h4>
                    <p className="text-small leading-[1.55] text-white/50">{body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────────────── */}
      <section id="how-it-works" className="bg-surface-page px-5 md:px-12 py-20 md:py-[120px]">
        <div className="max-w-content mx-auto">
          <div className="text-center mb-[72px]">
            <Reveal>
              <p className="text-[0.6875rem] font-bold tracking-[0.12em] uppercase text-primary-400 mb-5">How it works</p>
              <h2 className="text-[clamp(2rem,4vw,3.4rem)] font-extrabold leading-[1.05] tracking-[-0.03em] text-neutral-900 mb-4">
                Research done right,<br />in seconds.
              </h2>
              <p className="text-[1.0625rem] leading-[1.7] text-neutral-600 max-w-[520px] mx-auto">
                Tell Clean Shopper what you need. It does the hard work — cross-referencing ingredient databases, reading the fine print, surfacing what matters to you.
              </p>
            </Reveal>
          </div>

          <Reveal delay={100}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-200 rounded-lg overflow-hidden">
              {[
                {
                  num: '01',
                  title: 'Describe what you need',
                  body: 'Type in plain language — "a dish soap without SLS" or "a face wash safe for pregnancy." No need to know the chemical names.',
                },
                {
                  num: '02',
                  title: 'Get ingredient-rated results',
                  body: 'Clean Shopper searches the web in real time, researches ingredients across available sources, and explains what it found — in plain language you can actually use.',
                },
                {
                  num: '03',
                  title: 'Save what you trust',
                  body: 'Save researched products to your personal library. View the full ingredient list for any product — with safety ratings and what to watch for.',
                },
              ].map(({ num, title, body }) => (
                <div key={num} className="bg-surface-card p-8 md:p-10">
                  <div className="text-[4rem] font-black leading-none tracking-[-0.04em] text-primary-100 mb-5">{num}</div>
                  <div className="w-8 h-[3px] bg-primary-400 rounded-full mb-5" aria-hidden="true" />
                  <h3 className="text-h4 font-bold text-neutral-900 tracking-[-0.02em] mb-3">{title}</h3>
                  <p className="text-[0.9375rem] leading-[1.65] text-neutral-600">{body}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────────────── */}
      <section id="features" className="bg-primary-900 px-5 md:px-12 py-20 md:py-[120px]">
        <div className="max-w-content mx-auto">
          <Reveal>
            <p className="text-[0.6875rem] font-bold tracking-[0.12em] uppercase text-primary-300 mb-5">Features</p>
            <h2 className="text-[clamp(2rem,4vw,3.4rem)] font-extrabold leading-[1.05] tracking-[-0.03em] text-white mb-14">
              Everything you need.<br />Nothing you don't.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Wide card: AI research */}
            <Reveal className="md:col-span-2">
              <div className="bg-white/[0.05] border border-white/[0.09] rounded-2xl p-8 md:p-10 hover:bg-white/[0.08] hover:border-primary-400/35 hover:-translate-y-[3px] transition-[background,border-color,transform] duration-normal grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div>
                  <IconBox>
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                      <path d="M11 3C6.58 3 3 6.58 3 11s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 2c.68 0 1.35.1 1.98.27L5.27 12.98A6.04 6.04 0 015 11c0-3.31 2.69-6 6-6zm0 12c-.68 0-1.35-.1-1.98-.27l7.71-7.71c.17.63.27 1.3.27 1.98 0 3.31-2.69 6-6 6z" fill="#4BB884"/>
                    </svg>
                  </IconBox>
                  <h3 className="text-h4 font-bold text-white tracking-[-0.02em] mb-3">AI ingredient research</h3>
                  <p className="text-[0.9375rem] leading-[1.65] text-white/55">
                    Describe what you're looking for. Clean Shopper searches the web in real time, researches ingredients from available sources, and surfaces recommendations with full ingredient reasoning — not just a score.
                  </p>
                  <div className="flex gap-2 flex-wrap mt-5">
                    {['Real-time web search', 'Plain-language reasoning', 'Ingredient analysis'].map(t => <FeatureTag key={t} label={t} />)}
                  </div>
                </div>

                {/* Mock UI */}
                <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6">
                  <div className="text-right mb-4">
                    <span className="inline-block bg-primary-500 text-white text-small leading-[1.5] px-4 py-[10px] rounded-xl rounded-br-sm max-w-[85%] text-left">
                      Find me a laundry detergent without fragrance or optical brighteners
                    </span>
                  </div>
                  <div className="mb-3">
                    <span className="inline-block bg-white/[0.08] text-white/80 text-small leading-[1.5] px-4 py-[10px] rounded-xl rounded-bl-sm max-w-[85%]">
                      Found 3 options that meet your criteria. Here are the top picks with full ingredient analysis:
                    </span>
                  </div>
                  {[
                    { score: '1', name: "Molly's Suds Original Laundry Powder", meta: 'EWG score 1–2 · Fragrance-free · No optical brighteners', warn: false },
                    { score: '3', name: 'Seventh Generation Free & Clear', meta: 'EWG score 3 · 1 ingredient flagged for review', warn: true },
                  ].map(({ score, name, meta, warn }) => (
                    <div key={name} className="bg-white/[0.06] border border-white/10 rounded-[10px] px-4 py-[14px] mt-[10px] flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-[0.75rem] font-extrabold flex-shrink-0 ${
                        warn
                          ? 'bg-[rgba(196,138,26,0.2)] border border-[rgba(196,138,26,0.35)] text-[#E8A83A]'
                          : 'bg-success/25 border border-success/40 text-primary-300'
                      }`}>{score}</div>
                      <div>
                        <div className="text-small font-semibold text-white/90">{name}</div>
                        <div className="text-[0.75rem] text-white/40 mt-0.5">{meta}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Small feature cards */}
            {[
              {
                title: 'Browse and filter by safety',
                body: 'Browse a curated catalog across 6 categories and filter by safety rating — clean, caution, or avoid. Every product surfaces its rating upfront, so you can narrow down without reading every label.',
                tags: ['6 categories', 'Filter by safety rating', 'Curated catalog'],
                icon: <path d="M4 6h14M4 11h9M4 16h6" stroke="#4BB884" strokeWidth="1.8" strokeLinecap="round"/>,
                delay: 100,
              },
              {
                title: 'Persistent shopping list',
                body: "Add any recommended or saved product to a shopping list that persists across sessions. Everything in one place, ready when you're at the store or ordering online.",
                tags: ['Persists across sessions', 'Any product'],
                icon: <><path d="M7 10l2.5 2.5L15 7" stroke="#4BB884" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><rect x="3" y="3" width="16" height="16" rx="4" stroke="#4BB884" strokeWidth="1.5"/></>,
                delay: 200,
              },
              {
                title: 'Full ingredient transparency',
                body: 'Click any product to see every ingredient with its safety rating and what to watch for. Color-coded for quick scanning — green for safe, orange for caution, red to avoid.',
                tags: ['Per-ingredient ratings', 'Color-coded', 'No chemistry degree required'],
                icon: <path d="M4 11h14M11 4v14" stroke="#4BB884" strokeWidth="1.8" strokeLinecap="round"/>,
                delay: 100,
              },
              {
                title: 'Personal product library',
                body: "Save researched products to a library organized by category. Build your reference set of clean products over time — never re-research something you've already vetted.",
                tags: ['Organized by category', 'Always up to date'],
                icon: <><rect x="3" y="4" width="4" height="14" rx="1" stroke="#4BB884" strokeWidth="1.5"/><rect x="9" y="4" width="4" height="14" rx="1" stroke="#4BB884" strokeWidth="1.5"/><rect x="15" y="4" width="4" height="14" rx="1" stroke="#4BB884" strokeWidth="1.5"/></>,
                delay: 200,
              },
            ].map(({ title, body, tags, icon, delay }) => (
              <Reveal key={title} delay={delay} className="flex flex-col">
                <div className="flex-1 bg-white/[0.05] border border-white/[0.09] rounded-2xl p-8 md:p-10 hover:bg-white/[0.08] hover:border-primary-400/35 hover:-translate-y-[3px] transition-[background,border-color,transform] duration-normal flex flex-col">
                  <IconBox>
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">{icon}</svg>
                  </IconBox>
                  <h3 className="text-h4 font-bold text-white tracking-[-0.02em] mb-3">{title}</h3>
                  <p className="text-[0.9375rem] leading-[1.65] text-white/55 flex-1">{body}</p>
                  <div className="flex gap-2 flex-wrap mt-5">
                    {tags.map(t => <FeatureTag key={t} label={t} />)}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Stats */}
          <Reveal className="mt-20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06] rounded-2xl overflow-hidden">
              {[
                { num: '144+',  label: 'Curated products across\n6 categories, ready to explore' },
                { num: '<30s',  label: 'Average AI response time\nfor ingredient research' },
                { num: 'Live',  label: 'Real-time web research\npowers every AI query' },
              ].map(({ num, label }) => (
                <div key={num} className="p-10 text-center bg-white/[0.03]">
                  <div className="text-[3rem] font-black tracking-[-0.04em] text-primary-300 leading-none mb-2">{num}</div>
                  <div className="text-small text-white/50 leading-[1.4] whitespace-pre-line">{label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Testimonials ───────────────────────────────────────────────── */}
      <section id="testimonials" className="bg-sand-100 px-5 md:px-12 py-20 md:py-[120px]">
        <div className="max-w-content mx-auto">
          <Reveal>
            <p className="text-[0.6875rem] font-bold tracking-[0.12em] uppercase text-primary-400 mb-5">What people are saying</p>
            <h2 className="text-[clamp(2rem,4vw,3.4rem)] font-extrabold leading-[1.05] tracking-[-0.03em] text-neutral-900 mb-14">
              Finally, a tool<br />that gets it.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "I used to spend 45 minutes comparing products on EWG before every Target run. Now I just ask Clean Shopper and have an answer in under a minute, with reasoning I actually trust.",
                name: 'Jess M.', handle: 'Mom of two · Seattle, WA', initials: 'JM', delay: 0,
              },
              {
                quote: "I asked it to find a body wash without synthetic fragrance and it came back with three options and full ingredient breakdowns. It did in 30 seconds what I'd spend half an hour Googling.",
                name: 'Riya K.', handle: 'Wellness enthusiast · Austin, TX', initials: 'RK', delay: 100,
              },
              {
                quote: "I've been eczema-prone my whole life and spent years figuring out what's safe. Clean Shopper found alternatives to my entire shower routine in one conversation. I was skeptical. I'm not anymore.",
                name: 'Tom P.', handle: 'Sensitive skin · Brooklyn, NY', initials: 'TP', delay: 200,
              },
            ].map(({ quote, name, handle, initials, delay }) => (
              <Reveal key={name} delay={delay} className="flex flex-col">
                <div className="flex-1 flex flex-col bg-surface-card border border-neutral-200 rounded-2xl p-8 shadow-sm hover:shadow-md hover:-translate-y-[3px] transition-[box-shadow,transform] duration-normal">
                  <div className="flex gap-0.5 mb-4" aria-label="5 stars">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-[14px] h-[14px]" viewBox="0 0 16 16" fill="#C49456" aria-hidden="true">
                        <path d="M8 1l1.85 3.75L14 5.5l-3 2.92.71 4.13L8 10.4 4.29 12.55 5 8.42 2 5.5l4.15-.75L8 1z"/>
                      </svg>
                    ))}
                  </div>
                  <div className="flex-1">
                    <span className="block text-[2.5rem] leading-none text-primary-200 font-extrabold -mb-1" aria-hidden="true">{'“'}</span>
                    <p className="text-body leading-[1.65] text-neutral-700 mb-6">{quote}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 border-2 border-primary-200 flex items-center justify-center text-small font-bold text-primary-600 flex-shrink-0" aria-hidden="true">
                      {initials}
                    </div>
                    <div>
                      <div className="text-[0.9rem] font-bold text-neutral-900 tracking-[-0.01em]">{name}</div>
                      <div className="text-[0.8125rem] text-neutral-500">{handle}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────────── */}
      <section id="get-started" className="relative bg-primary-800 px-5 md:px-12 py-20 md:py-[120px] overflow-hidden">
        <div className="absolute -top-[200px] -right-[200px] w-[600px] h-[600px] [background:radial-gradient(circle,rgba(40,163,104,0.15)_0%,transparent_70%)] pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-[150px] -left-[150px] w-[500px] h-[500px] [background:radial-gradient(circle,rgba(46,138,154,0.1)_0%,transparent_70%)] pointer-events-none" aria-hidden="true" />

        <div className="relative z-10 max-w-[640px] mx-auto text-center">
          <Reveal>
            <p className="text-[0.6875rem] font-bold tracking-[0.12em] uppercase text-primary-300 mb-5">Get started today</p>
            <h2 className="text-[clamp(2rem,4vw,3.4rem)] font-extrabold leading-[1.05] tracking-[-0.03em] text-white mb-5">
              Clean choices<br />start here.
            </h2>
            <p className="text-[1.0625rem] leading-[1.7] text-white/60 mb-10 max-w-[520px] mx-auto">
              Create a free account. Start researching in under a minute. No credit card, no commitment.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button type="button" onClick={() => onNavigate('signup')}
                className="inline-flex items-center justify-center bg-primary-400 hover:bg-primary-300 text-white text-body font-bold tracking-[0.01em] px-8 py-[14px] rounded-md transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-300 focus:ring-offset-2 focus:ring-offset-primary-800">
                Create free account
              </button>
              <button type="button" onClick={() => onNavigate('signin')}
                className="inline-flex items-center justify-center bg-white/[0.08] border border-white/[0.18] hover:bg-white/[0.14] text-white text-body font-semibold px-8 py-[14px] rounded-md transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-800">
                Sign in
              </button>
            </div>
            <p className="mt-4 text-[0.8125rem] text-white/30">
              Free forever for personal use &nbsp;·&nbsp; No credit card required
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="bg-primary-900 border-t border-white/[0.06] px-5 md:px-12 py-12">
        <div className="max-w-content mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-[10px]">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" className="flex-shrink-0">
                <rect width="32" height="32" rx="8" fill="#36A85B"/>
                <g transform="translate(6 6) scale(0.4167)" stroke="#FFFFFF" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M24 5 C 13.5 13, 13.5 31, 24 43 C 34.5 31, 34.5 13, 24 5 Z"/>
                  <path d="M17.5 25 L22 30 L31 16.5"/>
                </g>
              </svg>
              <span className="text-[1.0625rem] font-bold text-white tracking-[-0.01em]">Clean Shopper</span>
            </div>
            <span className="text-[0.8125rem] text-white/30">© 2026 Clean Shopper. All rights reserved.</span>
          </div>
          <ul className="flex gap-6 list-none">
            {['Privacy', 'Terms', 'About', 'Contact'].map(link => (
              <li key={link}>
                <button type="button" className="text-[0.8125rem] text-white/40 hover:text-white/75 transition-colors duration-fast focus:outline-none">
                  {link}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </footer>

    </div>
  );
}
